import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalText, ModalTitle} from '../components/common/ui';
import {Icon} from '../components/common/Icon';
import {StoreBadges} from '../components/common/StoreBadges';
import {FunctionsError} from '../services/functionsService';
import premiumClaw from '../assets/premium_claw.png';
import posthog, {isPostHogConfigured} from '../posthog';

type UpgradeReason =
  | 'FREE_LIMIT_REACHED'
  | 'AI_BUDGET_EXCEEDED'
  | 'VOICE_MESSAGE_LIMIT_REACHED'
  | 'generic';

interface UpgradeContextValue {
  showUpgrade: (reason?: UpgradeReason) => void;
  /** Shows the upgrade dialog when the error is a plan-limit error. */
  handleLimitError: (error: unknown) => boolean;
}

const UpgradeContext = createContext<UpgradeContextValue>({
  showUpgrade: () => {},
  handleLimitError: () => false,
});

const REASON_KEYS: Record<UpgradeReason, string> = {
  FREE_LIMIT_REACHED: 'FreeLimitReached',
  AI_BUDGET_EXCEEDED: 'AiBudgetExceeded',
  VOICE_MESSAGE_LIMIT_REACHED: 'VoiceMessageLimitReached',
  generic: 'upgrade_premium',
};

export function UpgradeProvider({children}: {children: ReactNode}) {
  const {t} = useTranslation();
  const [reason, setReason] = useState<UpgradeReason | null>(null);

  // Web has no checkout — the dialog sends users to the mobile stores, so
  // this event is the top of the web → app upgrade funnel.
  const openUpgrade = useCallback((r: UpgradeReason) => {
    setReason(r);
    if (isPostHogConfigured) {
      posthog.capture('upgrade_prompt_shown', {reason: r});
    }
  }, []);

  const showUpgrade = useCallback(
    (r: UpgradeReason = 'generic') => openUpgrade(r),
    [openUpgrade],
  );

  const handleLimitError = useCallback(
    (error: unknown): boolean => {
      if (
        error instanceof FunctionsError &&
        (error.code === 'FREE_LIMIT_REACHED' ||
          error.code === 'AI_BUDGET_EXCEEDED' ||
          error.code === 'VOICE_MESSAGE_LIMIT_REACHED')
      ) {
        openUpgrade(error.code as UpgradeReason);
        return true;
      }
      return false;
    },
    [openUpgrade],
  );

  return (
    <UpgradeContext.Provider value={{showUpgrade, handleLimitError}}>
      {children}
      <Modal open={reason !== null} onClose={() => setReason(null)}>
        {reason && (
          <div style={{textAlign: 'center', position: 'relative'}}>
            <img
              src={premiumClaw}
              alt=""
              style={{
                position: 'absolute',
                right: -26,
                bottom: -26,
                width: 96,
                opacity: 0.9,
                pointerEvents: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setReason(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                color: 'var(--color-text-secondary)',
                background: 'var(--color-glass)',
              }}
            >
              <Icon name="x" size={16} />
            </button>
            <div
              style={{
                width: 60,
                height: 60,
                margin: '0 auto 14px',
                borderRadius: 18,
                background: 'var(--gradient-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                boxShadow: 'var(--glow-purple)',
              }}
            >
              <Icon name="crown" size={30} />
            </div>
            <ModalTitle>{t(REASON_KEYS[reason])}</ModalTitle>
            <ModalText>{t('WebUpgradeOnMobile')}</ModalText>
            <ModalText>{t('WebSameAccountHint')}</ModalText>
            <StoreBadges placement="upgrade_prompt" />
          </div>
        )}
      </Modal>
    </UpgradeContext.Provider>
  );
}

export const useUpgrade = () => useContext(UpgradeContext);
